const LINE_ALIGN_PREFIX = /^\[a:(left|center|right)\]\s*/i;

function parseLineAlignPrefix(line) {
  const s = String(line || '');
  const m = s.match(LINE_ALIGN_PREFIX);
  if (!m) return { align: 'left', body: s };
  const align = String(m[1] || 'left').toLowerCase();
  const a = align === 'center' || align === 'right' ? align : 'left';
  return { align: a, body: s.replace(LINE_ALIGN_PREFIX, '') };
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function legacyInlineToHtml(text) {
  let raw = String(text || '');
  raw = raw.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  raw = raw.replace(
    /\[color:(#[0-9a-fA-F]{3,8})\]([\s\S]*?)\[\/color\]/gi,
    (_m, hex, inner) => `<span style="color:${hex}">${legacyInlineToHtml(inner)}</span>`
  );
  return raw;
}

/** @param {string} raw */
export function isNotesHtml(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  return /^<[a-z][\s\S]*>/i.test(s);
}

/** Convert legacy plain-text notes markup into HTML for TipTap. */
export function legacyNotesPlainToHtml(plain) {
  const lines = String(plain || '').split('\n');
  const htmlParts = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (!bulletBuffer.length) return;
    htmlParts.push(`<ul>${bulletBuffer.map((item) => `<li>${item}</li>`).join('')}</ul>`);
    bulletBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushBullets();
      htmlParts.push('<p></p>');
      continue;
    }
    const { align, body } = parseLineAlignPrefix(line);
    const alignStyle = align !== 'left' ? ` style="text-align:${align}"` : '';

    if (/^---+$/.test(body.trim())) {
      flushBullets();
      htmlParts.push('<hr />');
      continue;
    }

    if (body.startsWith('- [ ] ')) {
      flushBullets();
      htmlParts.push(`<p${alignStyle}>☐ ${legacyInlineToHtml(body.slice(6))}</p>`);
      continue;
    }

    if (body.startsWith('- ')) {
      bulletBuffer.push(legacyInlineToHtml(body.slice(2)));
      continue;
    }

    flushBullets();
    if (body.startsWith('## ')) {
      htmlParts.push(`<h3${alignStyle}>${legacyInlineToHtml(body.slice(3))}</h3>`);
    } else {
      htmlParts.push(`<p${alignStyle}>${legacyInlineToHtml(body)}</p>`);
    }
  }

  flushBullets();
  return htmlParts.join('') || '<p></p>';
}

export function normalizeNotesContentForEditor(raw) {
  const s = String(raw || '').trim();
  if (!s) return '<p></p>';
  if (isNotesHtml(s)) return s;
  return legacyNotesPlainToHtml(s);
}

/** True when stored tab text looks like HTML (notes, rider, etc.). */
export function isHtmlTabContent(text) {
  const s = String(text || '');
  if (!s.trim()) return false;
  if (!s.trimStart().startsWith('<')) return false;
  return /<(p|div|ul|ol|h[1-6]|br|strong|em|u|blockquote|span)\b/i.test(s);
}

/** Strip notes/rider HTML to readable plain text (preserves line breaks when possible). */
export function notesHtmlToPlainText(html) {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (!isNotesHtml(raw) && !isHtmlTabContent(raw)) return raw;
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = raw;
    return (el.innerText || el.textContent || '').trim();
  }
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Short plain preview for calendar cells. */
export function htmlToPlainPreview(html, maxLen = 220) {
  const plain = notesHtmlToPlainText(html);
  if (!plain) return '';
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen - 1)}…`;
}
